import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import { Organization, OrganizationType } from '../types/organization.type';
import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractApiErrorMessage } from '../../shared/utils/extract-api-error-message.util';
import { ADMIN_ORGANIZATIONS_API_PATHS } from '../constants/organizations-paths.constants';
import { BaseParams } from '../../shared/types/base-params.type';

interface GetOrganizationsFilters {
  type?: OrganizationType;
  name?: string;
}

interface UseAdminGetOrganizationsParams extends BaseParams {
  filters: GetOrganizationsFilters;
}

interface GetOrganizationsState {
  isLoading: boolean;
  totalCount: number;
  organizations: Organization[];
}

interface GetOrganizationsResponse {
  organizations: Organization[];
  totalCount: number;
}

const INITIAL_STATE: GetOrganizationsState = {
  isLoading: true,
  totalCount: 0,
  organizations: [],
};

export function useAdminGetOrganizations({ page, limit, sortDirection, filters }: UseAdminGetOrganizationsParams) {
  const { enqueueSnackbar } = useSnackbar();
  const { type, name } = filters;
  const [result, setResult] = useState(INITIAL_STATE);

  useEffect(() => {
    setResult(INITIAL_STATE);

    HttpClient.get<GetOrganizationsResponse>(ADMIN_ORGANIZATIONS_API_PATHS.GET_ORGANIZATIONS, {
      params: { page, limit, sortDirection, filters: { type, name } },
    })
      .then((response) => {
        setResult({
          isLoading: false,
          totalCount: response.totalCount,
          organizations: response.organizations,
        });
      })
      .catch((error) => {
        enqueueSnackbar(
          extractApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des organisations'),
          { variant: 'error' }
        );
        setResult({ isLoading: false, totalCount: 0, organizations: [] });
      });
  }, [page, limit, sortDirection, type, name]);

  return result;
}
